import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    Puzzle,
    Plus,
    Trash2,
    Pencil,
    Upload,
    Save,
    PowerOff,
    Power,
    Shield,
    MousePointerClick,
    Type,
    CheckSquare,
    HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "../lib/auth";
import {
    listPlugins,
    listPluginPrompts,
    createPluginPrompt,
    updatePluginPrompt,
    deletePluginPrompt,
    updatePlugin,
    deletePlugin,
    uploadPluginIcon,
    getPluginAdmin,
    updateMasterPrompts,
} from "../lib/api";

/**
 * Plugin detail page (iter-45).
 *
 * Open to all signed-in users — shows the plugin and lets them CRUD their
 * own saved prompts for it (organised by Estimate / Analyze kind).
 *
 * Superadmins get an extra set of controls at the top: rename, change
 * accent, upload a new image/video, enable/disable, and delete. All
 * changes reflect everywhere else (workspace floating bar, /plugins
 * catalog, Analyze/Estimate dialog prompt boxes).
 */
export default function PluginDetailPage() {
    const { pluginId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isSuperadmin = user?.role === "superadmin";
    const isAdmin = isSuperadmin || user?.role === "admin";

    const [plugin, setPlugin] = useState(null);
    const [prompts, setPrompts] = useState([]);
    const [masterPrompts, setMasterPrompts] = useState(null); // admin+ only
    const [loading, setLoading] = useState(true);

    const reload = async () => {
        setLoading(true);
        try {
            const [cat, list] = await Promise.all([
                listPlugins(),
                listPluginPrompts({ pluginId }),
            ]);
            const found = cat.find((p) => p.id === pluginId);
            setPlugin(found || null);
            setPrompts(list);
            // Admin+ gets the master prompts via the elevated endpoint.
            if (isAdmin && found) {
                try {
                    const full = await getPluginAdmin(pluginId);
                    setMasterPrompts({
                        estimate: full.master_prompt_estimate || "",
                        analyze: full.master_prompt_analyze || "",
                    });
                } catch {
                    setMasterPrompts(null);
                }
            } else {
                setMasterPrompts(null);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load plugin.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pluginId]);

    const byKind = useMemo(() => {
        // Kept for legacy callers — main UI now uses the flat `prompts`
        // list. (Add-on Buttons section was merged in iter-78.)
        const out = { estimate: [], analyze: [] };
        for (const p of prompts) {
            (out[p.kind] || out.estimate).push(p);
        }
        return out;
    }, [prompts]);

    if (loading) {
        return (
            <div
                className="h-[100dvh] w-full overflow-y-auto bg-[#0a0a0a] text-slate-200 flex items-center justify-center"
                data-testid="plugin-detail-loading"
            >
                Loading…
            </div>
        );
    }
    if (!plugin) {
        return (
            <div className="h-[100dvh] w-full overflow-y-auto bg-[#0a0a0a] text-slate-200 flex flex-col">
                <main className="flex-1 flex items-center justify-center p-6">
                    <div className="text-center">
                        <div className="text-sm text-slate-400 mb-3">
                            Plugin not found.
                        </div>
                        <Button
                            variant="outline"
                            onClick={() => navigate("/plugins")}
                            data-testid="plugin-detail-back-empty"
                        >
                            Back to plugins
                        </Button>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div
            className="h-[100dvh] w-full overflow-y-auto bg-[#0a0a0a] text-slate-100 flex flex-col"
            data-testid="plugin-detail-page"
        >
            <main className="flex-1 px-4 sm:px-8 py-6 max-w-4xl mx-auto w-full pb-24">
                <button
                    type="button"
                    onClick={() => navigate("/plugins")}
                    data-testid="plugin-detail-back"
                    className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-white mb-4"
                >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    All plugins
                </button>

                <PluginHeader
                    plugin={plugin}
                    isSuperadmin={isSuperadmin}
                    onChanged={reload}
                    onDeleted={() => navigate("/plugins")}
                />

                {isAdmin && masterPrompts && (
                    <MasterPromptsEditor
                        pluginId={pluginId}
                        initial={masterPrompts}
                        onSaved={reload}
                    />
                )}

                <div className="mt-6 space-y-6">
                    <PromptsSection
                        plugin={plugin}
                        prompts={prompts}
                        onChanged={reload}
                        isSuperadmin={isSuperadmin}
                    />
                </div>
            </main>
        </div>
    );
}

function PluginHeader({ plugin, isSuperadmin, onChanged, onDeleted }) {
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({
        name: plugin.name || "",
        description: plugin.description || "",
        accent: plugin.accent || "#00A3FF",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm({
            name: plugin.name || "",
            description: plugin.description || "",
            accent: plugin.accent || "#00A3FF",
        });
    }, [plugin.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = async () => {
        setSaving(true);
        try {
            await updatePlugin(plugin.id, {
                name: form.name,
                description: form.description,
                accent: form.accent,
            });
            toast.success("Plugin updated");
            setEditing(false);
            onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update plugin.");
        } finally {
            setSaving(false);
        }
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            await uploadPluginIcon(plugin.id, file);
            toast.success("Icon updated");
            onChanged();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't upload icon.",
            );
        }
    };

    const handleToggleStatus = async () => {
        const next = plugin.status === "active" ? "disabled" : "active";
        try {
            await updatePlugin(plugin.id, { status: next });
            toast.success(next === "active" ? "Enabled" : "Disabled");
            onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't toggle.");
        }
    };

    const handleDelete = async () => {
        // eslint-disable-next-line no-alert
        if (
            !window.confirm(
                `Delete "${plugin.name}" and ALL saved prompts for every user? This cannot be undone.`,
            )
        )
            return;
        try {
            await deletePlugin(plugin.id);
            toast.success("Plugin deleted");
            onDeleted();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't delete.");
        }
    };

    const disabled = plugin.status !== "active";

    return (
        <div
            className="rounded-xl border border-white/15 bg-[#111] p-4 sm:p-5"
            style={{ borderLeft: `4px solid ${plugin.accent || "#00A3FF"}` }}
            data-testid="plugin-detail-header"
        >
            <div className="flex flex-row items-start gap-4">
                <div className="relative shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden bg-white/[0.04]">
                    {plugin.icon_type === "video" && plugin.icon ? (
                        <video
                            src={plugin.icon}
                            poster={plugin.poster || undefined}
                            autoPlay
                            muted
                            loop
                            playsInline
                            className="w-full h-full object-cover"
                        />
                    ) : plugin.icon ? (
                        <img
                            src={plugin.icon}
                            alt={plugin.name}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div
                            className="w-full h-full flex items-center justify-center font-black text-2xl"
                            style={{
                                color: "#0a1128",
                                backgroundColor: plugin.accent || "#334155",
                            }}
                        >
                            {plugin.name[0]}
                        </div>
                    )}
                    {isSuperadmin && (
                        <label
                            className="absolute inset-0 flex items-center justify-center bg-white/[0.05] opacity-0 hover:opacity-100 cursor-pointer transition-opacity text-white text-[11px] font-semibold"
                            data-testid="plugin-icon-upload-label"
                        >
                            <Upload className="w-4 h-4 mr-1" />
                            Change
                            <input
                                type="file"
                                accept="image/*,video/*"
                                className="hidden"
                                onChange={handleUpload}
                                data-testid="plugin-icon-upload-input"
                            />
                        </label>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    {editing ? (
                        <div className="space-y-2">
                            <Input
                                value={form.name}
                                onChange={(e) =>
                                    setForm({ ...form, name: e.target.value })
                                }
                                placeholder="Plugin name"
                                data-testid="plugin-edit-name"
                                className="bg-[#0a0a0a] border-white/15 text-white"
                            />
                            <Textarea
                                value={form.description}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        description: e.target.value,
                                    })
                                }
                                placeholder="Description"
                                rows={2}
                                data-testid="plugin-edit-description"
                                className="bg-[#0a0a0a] border-white/15 text-white"
                            />
                            <div className="flex items-center gap-2">
                                <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                                    Accent
                                </label>
                                <input
                                    type="color"
                                    value={form.accent}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            accent: e.target.value,
                                        })
                                    }
                                    className="w-10 h-8 rounded border border-white/15 bg-[#0a0a0a] cursor-pointer"
                                    data-testid="plugin-edit-accent"
                                />
                            </div>
                            <div className="flex gap-2 justify-end pt-1">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setEditing(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={handleSave}
                                    disabled={saving}
                                    data-testid="plugin-edit-save"
                                    className="bg-[#1a3fd1] hover:bg-[#1a3fd1]/80"
                                >
                                    <Save className="w-3.5 h-3.5 mr-1" />
                                    Save
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-start gap-2">
                                <h1
                                    className="text-xl sm:text-2xl font-black tracking-tight flex-1"
                                    style={{ color: plugin.accent || "#ffffff" }}
                                    data-testid="plugin-detail-name"
                                >
                                    {plugin.name}
                                </h1>
                                {disabled && (
                                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border border-amber-500/60 text-amber-300 bg-amber-500/10">
                                        Disabled
                                    </span>
                                )}
                            </div>
                            <p className="mt-1 text-[13px] text-slate-400 leading-relaxed">
                                {plugin.description}
                            </p>
                            {isSuperadmin && (
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setEditing(true)}
                                        data-testid="plugin-edit-open"
                                        className="border-white/15 text-slate-200 hover:bg-white/10"
                                    >
                                        <Pencil className="w-3.5 h-3.5 mr-1" />
                                        Edit
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleToggleStatus}
                                        data-testid="plugin-toggle-status"
                                        className="border-white/15 text-slate-200 hover:bg-white/10"
                                    >
                                        {disabled ? (
                                            <>
                                                <Power className="w-3.5 h-3.5 mr-1" />
                                                Enable
                                            </>
                                        ) : (
                                            <>
                                                <PowerOff className="w-3.5 h-3.5 mr-1" />
                                                Disable
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleDelete}
                                        data-testid="plugin-delete"
                                        className="border-red-600/50 text-red-300 hover:bg-red-500/10"
                                    >
                                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                                        Delete
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function PromptsSection({ plugin, prompts, onChanged, isSuperadmin }) {
    const [creating, setCreating] = useState(false);
    return (
        <section className="space-y-2" data-testid="prompt-section-addon">
            <div className="flex items-center justify-between">
                <h2 className="text-[12px] uppercase tracking-[0.16em] text-slate-300 font-bold flex items-center gap-1.5">
                    <Puzzle className="w-3.5 h-3.5 text-[#8fa4f0]" />
                    Add-on Buttons
                    <span className="text-slate-500 font-normal">
                        · {prompts.length}
                    </span>
                </h2>
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setCreating(true)}
                    data-testid="prompt-section-addon-new"
                    className="text-[11px] h-7 text-[#8fa4f0] hover:text-white hover:bg-[#1a3fd1]/20"
                >
                    <Plus className="w-3 h-3 mr-1" />
                    New
                </Button>
            </div>
            <div className="space-y-1.5">
                {creating && (
                    <PromptEditor
                        kind="estimate"
                        plugin={plugin}
                        onCancel={() => setCreating(false)}
                        onSaved={() => {
                            setCreating(false);
                            onChanged();
                        }}
                    />
                )}
                {prompts.length === 0 && !creating && (
                    <div className="text-[12px] text-slate-500 italic py-2">
                        No add-on buttons yet. Tap <strong>New</strong> to add one.
                    </div>
                )}
                {prompts.map((pr) => (
                    <PromptRow
                        key={pr.id}
                        prompt={pr}
                        onChanged={onChanged}
                        isSuperadmin={isSuperadmin}
                    />
                ))}
            </div>
        </section>
    );
}

function PromptRow({ prompt, onChanged, isSuperadmin }) {
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({
        title: prompt.title || "",
        body: prompt.body || "",
    });
    const [saving, setSaving] = useState(false);
    const permanent = prompt.permanent_scope || "none";

    const handleSave = async () => {
        setSaving(true);
        try {
            await updatePluginPrompt(prompt.id, form);
            toast.success("Saved");
            setEditing(false);
            onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        // eslint-disable-next-line no-alert
        if (!window.confirm("Delete this saved prompt?")) return;
        try {
            await deletePluginPrompt(prompt.id);
            toast.success("Deleted");
            onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't delete.");
        }
    };

    const handlePermanentChange = async (scope) => {
        try {
            await updatePluginPrompt(prompt.id, { permanent_scope: scope });
            toast.success(
                scope === "none"
                    ? "No longer permanent"
                    : `Set permanent: ${scope}`,
            );
            onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update.");
        }
    };

    if (editing) {
        return (
            <div
                className="rounded-sm border border-white/15 bg-[#111] p-2.5 space-y-2"
                data-testid={`prompt-row-edit-${prompt.id}`}
            >
                <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Title"
                    className="bg-[#0a0a0a] border-white/15 text-white text-sm"
                    data-testid={`prompt-row-title-${prompt.id}`}
                />
                <Textarea
                    rows={4}
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    placeholder="Prompt body"
                    className="bg-[#0a0a0a] border-white/15 text-white text-sm"
                    data-testid={`prompt-row-body-${prompt.id}`}
                />
                <div className="flex justify-end gap-2">
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={saving}
                        data-testid={`prompt-row-save-${prompt.id}`}
                        className="bg-[#1a3fd1] hover:bg-[#1a3fd1]/80"
                    >
                        <Save className="w-3.5 h-3.5 mr-1" />
                        Save
                    </Button>
                </div>
            </div>
        );
    }
    return (
        <div
            className={`rounded-sm border p-2.5 flex items-start gap-2 ${
                permanent !== "none"
                    ? "border-rose-500/50 bg-rose-500/[0.04]"
                    : "border-white/15 bg-[#111]"
            }`}
            data-testid={`prompt-row-${prompt.id}`}
        >
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <div className="text-[13px] font-semibold text-white truncate">
                        {prompt.title || "Untitled"}
                    </div>
                    <span
                        className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border shrink-0 ${
                            prompt.kind === "analyze"
                                ? "border-amber-400/50 text-amber-200 bg-amber-400/10"
                                : "border-sky-400/50 text-sky-200 bg-sky-400/10"
                        }`}
                        data-testid={`prompt-row-kind-${prompt.id}`}
                    >
                        {prompt.kind === "analyze" ? "Analyze" : "Estimate"}
                    </span>
                    {permanent !== "none" && (
                        <span
                            className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border border-rose-500/60 text-rose-300 bg-rose-500/10 shrink-0"
                            data-testid={`prompt-row-permanent-badge-${prompt.id}`}
                        >
                            Permanent · {permanent}
                        </span>
                    )}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-3 whitespace-pre-wrap">
                    {prompt.body}
                </div>
                {isSuperadmin && (
                    <div className="mt-1.5 flex items-center gap-1.5">
                        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                            Permanent:
                        </label>
                        <select
                            value={permanent}
                            onChange={(e) => handlePermanentChange(e.target.value)}
                            data-testid={`prompt-row-permanent-${prompt.id}`}
                            className="text-[11px] px-1.5 py-0.5 bg-[#0a0a0a] border border-white/15 rounded text-slate-200 cursor-pointer"
                        >
                            <option value="none">none</option>
                            <option value="estimate">estimate</option>
                            <option value="analyze">analyze</option>
                            <option value="both">both</option>
                        </select>
                    </div>
                )}
            </div>
            <button
                type="button"
                onClick={() => setEditing(true)}
                data-testid={`prompt-row-edit-btn-${prompt.id}`}
                className="p-1.5 rounded-sm text-slate-400 hover:text-white hover:bg-white/10"
                title="Edit"
            >
                <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
                type="button"
                onClick={handleDelete}
                data-testid={`prompt-row-delete-${prompt.id}`}
                className="p-1.5 rounded-sm text-slate-500 hover:text-red-400 hover:bg-red-500/10"
                title="Delete"
            >
                <Trash2 className="w-3.5 h-3.5" />
            </button>
        </div>
    );
}

function PromptEditor({ kind, plugin, onCancel, onSaved }) {
    const [form, setForm] = useState({
        title: "",
        body: "",
        input_type: "button",
        placeholder: "",
        options: [],
        kind: kind || "estimate",
    });
    const [saving, setSaving] = useState(false);
    const [newOption, setNewOption] = useState("");

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const addOption = () => {
        const label = newOption.trim();
        if (!label) return;
        setField("options", [
            ...form.options,
            { id: Math.random().toString(36).slice(2, 10), label },
        ]);
        setNewOption("");
    };
    const removeOption = (id) =>
        setField(
            "options",
            form.options.filter((o) => o.id !== id),
        );

    const handleSave = async () => {
        if (!form.body.trim()) {
            toast.error("Prompt body is required.");
            return;
        }
        if (
            form.input_type === "multi_select" &&
            form.options.length === 0
        ) {
            toast.error("Add at least one checkbox option.");
            return;
        }
        setSaving(true);
        try {
            await createPluginPrompt({
                plugin_id: plugin.id,
                kind: form.kind || kind,
                title: form.title.trim() || "Untitled prompt",
                body: form.body.trim(),
                input_type: form.input_type,
                placeholder: form.placeholder.trim(),
                options: form.options,
            });
            toast.success("Created");
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't create.");
        } finally {
            setSaving(false);
        }
    };
    // Body-field tooltip depends on the selected input type.
    const bodyHint =
        form.input_type === "text"
            ? "Use [input] anywhere in the prompt body. At run time, the user's text box value replaces it. If [input] isn't present, their text is appended."
            : form.input_type === "multi_select"
                ? "Use [selections] anywhere in the prompt body. At run time, the comma-separated list of checked options replaces it. If [selections] isn't present, the picks are appended."
                : `Plain toggle prompt — when the user turns this on, the entire body text is included in the ${kind} instructions as-is.`;
    return (
        <div
            className="rounded-sm border border-emerald-500/40 bg-[#111] p-2.5 space-y-2"
            data-testid={`prompt-new-${kind}`}
        >
            <Input
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
                placeholder="Title (shown on the button)"
                className="bg-[#0a0a0a] border-white/15 text-white text-sm"
                data-testid={`prompt-new-title-${kind}`}
            />
            <div className="flex items-center gap-2 text-[11px]">
                <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                    Show on:
                </label>
                <select
                    value={form.kind}
                    onChange={(e) => setField("kind", e.target.value)}
                    data-testid={`prompt-new-kind-${kind}`}
                    className="px-2 py-1 bg-[#0a0a0a] border border-white/15 rounded text-slate-200 cursor-pointer text-[11px]"
                >
                    <option value="estimate">Estimate run</option>
                    <option value="analyze">Analyze run</option>
                </select>
                <span className="text-[10px] text-slate-500">
                    Use the <strong className="text-slate-300">Permanent</strong> dropdown after saving for "both".
                </span>
            </div>
            <div className="space-y-1">
                <div className="flex items-center gap-1.5">
                    <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                        Prompt body
                    </label>
                    <span
                        className="inline-flex items-center gap-1 text-[10px] text-slate-500 cursor-help"
                        title={bodyHint}
                        data-testid={`prompt-new-body-hint-${kind}`}
                    >
                        <HelpCircle className="w-3 h-3" />
                        {form.input_type === "text" && "[input] → user text"}
                        {form.input_type === "multi_select" &&
                            "[selections] → checked options"}
                    </span>
                </div>
                <Textarea
                    rows={4}
                    value={form.body}
                    onChange={(e) => setField("body", e.target.value)}
                    placeholder={
                        form.input_type === "text"
                            ? "e.g. Focus on [input] and ignore irrelevant trades."
                            : form.input_type === "multi_select"
                                ? "e.g. Flag any of [selections] in your analysis."
                                : `Prompt body — the AI will follow these instructions when this button is active during a ${kind} run.`
                    }
                    className="bg-[#0a0a0a] border-white/15 text-white text-sm font-mono"
                    data-testid={`prompt-new-body-${kind}`}
                />
            </div>

            {/* --- Input-type selector --- */}
            <div className="pt-1 space-y-2 border-t border-white/10">
                <div className="flex items-center justify-between">
                    <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                        User input at run time
                    </label>
                </div>
                <div
                    className="grid grid-cols-3 gap-1.5"
                    data-testid={`prompt-new-input-type-${kind}`}
                >
                    <InputTypeOption
                        active={form.input_type === "button"}
                        onClick={() => setField("input_type", "button")}
                        icon={<MousePointerClick className="w-3.5 h-3.5" />}
                        label="Button"
                        hint="Toggle on/off"
                        testid={`prompt-new-input-type-button-${kind}`}
                    />
                    <InputTypeOption
                        active={form.input_type === "text"}
                        onClick={() => setField("input_type", "text")}
                        icon={<Type className="w-3.5 h-3.5" />}
                        label="Text input"
                        hint="Title + input box"
                        testid={`prompt-new-input-type-text-${kind}`}
                    />
                    <InputTypeOption
                        active={form.input_type === "multi_select"}
                        onClick={() => setField("input_type", "multi_select")}
                        icon={<CheckSquare className="w-3.5 h-3.5" />}
                        label="Multi-select"
                        hint="Checkboxes"
                        testid={`prompt-new-input-type-multi-${kind}`}
                    />
                </div>

                {form.input_type === "text" && (
                    <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1.5">
                            Placeholder text
                            <span
                                className="inline-flex items-center gap-1 text-slate-500 cursor-help"
                                title="Hint shown inside the input box at run time. Example: 'project type' or 'square footage'."
                            >
                                <HelpCircle className="w-3 h-3" />
                            </span>
                        </label>
                        <Input
                            value={form.placeholder}
                            onChange={(e) =>
                                setField("placeholder", e.target.value)
                            }
                            placeholder="e.g. project type"
                            className="bg-[#0a0a0a] border-white/15 text-white text-sm"
                            data-testid={`prompt-new-placeholder-${kind}`}
                        />
                    </div>
                )}

                {form.input_type === "multi_select" && (
                    <div className="space-y-2">
                        <label className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                            Checkbox options
                        </label>
                        <div className="flex items-center gap-1.5">
                            <Input
                                value={newOption}
                                onChange={(e) => setNewOption(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addOption();
                                    }
                                }}
                                placeholder="New option label — press Enter or +"
                                className="bg-[#0a0a0a] border-white/15 text-white text-sm"
                                data-testid={`prompt-new-option-input-${kind}`}
                            />
                            <Button
                                type="button"
                                size="sm"
                                onClick={addOption}
                                disabled={!newOption.trim()}
                                data-testid={`prompt-new-option-add-${kind}`}
                                className="bg-[#1a3fd1]/20 text-[#8fa4f0] hover:bg-[#1a3fd1]/30 border border-[#1a3fd1]/40"
                            >
                                <Plus className="w-3.5 h-3.5" />
                            </Button>
                        </div>
                        {form.options.length > 0 && (
                            <div
                                className="flex flex-wrap gap-1.5"
                                data-testid={`prompt-new-options-list-${kind}`}
                            >
                                {form.options.map((o) => (
                                    <div
                                        key={o.id}
                                        className="inline-flex items-center gap-1.5 bg-[#0a0a0a] border border-white/15 rounded-full pl-3 pr-1 py-1"
                                    >
                                        <span className="text-[12px] text-slate-200">
                                            {o.label}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => removeOption(o.id)}
                                            data-testid={`prompt-new-option-remove-${o.id}`}
                                            className="p-0.5 rounded-full text-slate-500 hover:text-rose-400 hover:bg-rose-500/10"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                    data-testid={`prompt-new-save-${kind}`}
                    className="bg-emerald-500 hover:bg-emerald-600"
                >
                    <Save className="w-3.5 h-3.5 mr-1" />
                    Save
                </Button>
            </div>
        </div>
    );
}

function InputTypeOption({ active, onClick, icon, label, hint, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            data-active={active ? "true" : "false"}
            className={`flex flex-col items-center justify-center gap-0.5 py-2 px-1 rounded-sm border transition-colors ${
                active
                    ? "bg-emerald-500/20 border-emerald-400 text-emerald-100 shadow-[0_0_0_2px_rgba(52,211,153,0.15)]"
                    : "bg-[#0a0a0a] border-white/15 text-slate-400 hover:text-white hover:border-slate-500"
            }`}
        >
            <div className="flex items-center gap-1">
                {icon}
                <span className="text-[11px] font-bold">{label}</span>
            </div>
            <span className="text-[9px] opacity-70">{hint}</span>
        </button>
    );
}

function MasterPromptsEditor({ pluginId, initial, onSaved }) {
    const [form, setForm] = useState({
        estimate: initial?.estimate || "",
        analyze: initial?.analyze || "",
    });
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        setForm({
            estimate: initial?.estimate || "",
            analyze: initial?.analyze || "",
        });
        setDirty(false);
    }, [initial?.estimate, initial?.analyze]);

    const handleChange = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setDirty(true);
    };
    const handleSave = async () => {
        setSaving(true);
        try {
            await updateMasterPrompts(pluginId, {
                master_prompt_estimate: form.estimate,
                master_prompt_analyze: form.analyze,
            });
            toast.success("Master prompts saved");
            setDirty(false);
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section
            className="mt-6 rounded-xl border border-rose-500/40 bg-rose-500/[0.04] p-4"
            data-testid="master-prompts-editor"
        >
            <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                    <h2 className="text-[12px] uppercase tracking-[0.16em] text-rose-200 font-bold flex items-center gap-1.5">
                        <Shield className="w-3.5 h-3.5" />
                        Master prompts
                        <span className="text-rose-400/70 font-normal normal-case tracking-normal">
                            · admin only · applies to every run for every user
                        </span>
                    </h2>
                    <p className="mt-1 text-[11px] text-slate-400">
                        Prepended to the plugin's system prompt with a{" "}
                        <code className="text-[10px] bg-[#0a0a0a] px-1 py-0.5 rounded border border-white/15">
                            [MASTER]
                        </code>{" "}
                        marker. Users never see these fields. Use them to
                        codify the house rules for this trade (what to measure, units, red-flags, etc.).
                    </p>
                </div>
                <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving || !dirty}
                    data-testid="master-prompts-save"
                    className="bg-rose-600 hover:bg-rose-700 shrink-0"
                >
                    <Save className="w-3.5 h-3.5 mr-1" />
                    Save
                </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <div>
                    <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                        Estimate master
                    </label>
                    <Textarea
                        rows={5}
                        value={form.estimate}
                        onChange={(e) => handleChange("estimate", e.target.value)}
                        placeholder="e.g. Always include topsoil strip at 6″, factor 25% swell, price haul-off separate."
                        data-testid="master-estimate"
                        className="mt-1 bg-[#0a0a0a] border-white/15 text-white text-[12px]"
                    />
                </div>
                <div>
                    <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                        Analyze master
                    </label>
                    <Textarea
                        rows={5}
                        value={form.analyze}
                        onChange={(e) => handleChange("analyze", e.target.value)}
                        placeholder="e.g. Always call out grading elevations, soil type, and drainage direction."
                        data-testid="master-analyze"
                        className="mt-1 bg-[#0a0a0a] border-white/15 text-white text-[12px]"
                    />
                </div>
            </div>
        </section>
    );
}

